import React from 'react';

import { useInterval } from '@mantine/hooks';

import type { UseLoadingProgressOptions } from './LoadingProgress.types';

/**
 * Hook that returns provider for LoadingProgress component
 * @returns {UseLoadingProgressProvider}
 */
export const useLoadingProgress = ({
	deps,
	stepInterval = 500,
	transitionDuration = 300,
	exitTransitionDuration = 400,
}: UseLoadingProgressOptions) => {
	const [progress, setProgress] = React.useState(0);
	const [loaded, setLoaded] = React.useState(false);

	const interval = useInterval(() => {
		setProgress((amount) => {
			let next = 0;
			if (amount >= 0 && amount <= 20) {
				next = 10;
			} else if (amount >= 20 && amount <= 50) {
				next = 4;
			} else if (amount >= 50 && amount <= 80) {
				next = 2;
			} else if (amount >= 80 && amount <= 99) {
				next = 0.5;
			}

			return amount + next;
		});
	}, stepInterval);

	const set = (value: React.SetStateAction<number>) => setProgress(value);
	const increment = (value: number) => setProgress((c) => Math.min(c + value, 100));
	const decrement = (value: number) => setProgress((c) => Math.max(c - value, 0));
	const start = () => {
		setLoaded(true);
		interval.stop();
		interval.start();
	};
	const stop = () => {
		interval.stop();
		set(100);
		setLoaded(false);
		setTimeout(() => {
			set(0);
		}, exitTransitionDuration);
	};

	React.useEffect(() => {
		const shouldTrigger = deps?.some((dep) => dep) || false;
		if (shouldTrigger) start();
		else stop();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps);

	return {
		provider: {
			progress,
			loaded,
			setLoaded,
			transitionDuration,
			exitTransitionDuration,
		},
		handlers: {
			set,
			increment,
			decrement,
			start,
			stop,
		},
	};
};
